import { Lightning as lng, Log, Registry } from '@lightningjs/sdk';
import { GuideTimeline } from './GuideTimeline';
import { GuideChannel } from './GuideChannel';
import { CmsConstants } from '../../../Business/cms/CmsConstants';
import { EpgGuideDataSource } from '../../../Business/epg/guide/EpgGuideDataSource';
import { GuideChannelDummy } from './GuideChannelDummy';
import { Theme } from '../../../config/theme';

const _constants = {
  timeLineHeight: 50,
  channelInfoWidth: 322,
  channelProgramsWidth: 1432,
  channelHeight: 80,
  channelSpacing: 30,
};

export class EpgGuide extends lng.Component {
  static _template() {
    return {
      x: 0,
      y: 0,
      clipping: true,
      w: w => w,
      h: h => h,
      Timeline: {
        type: GuideTimeline,
        h: _constants.timeLineHeight,
      },
      ClipBox: {
        clipping: true,
        w: w => w,
        h: h => h - _constants.timeLineHeight,
        y: _constants.timeLineHeight,
        ContainerContainer: {
          y: CmsConstants.Card.borderSize,
          w: w => w,
          h: h => h - CmsConstants.Card.borderSize,
          Container: {
            w: w => w,
          },
        },
      },
      TimeStripContainer: {
        x: _constants.channelInfoWidth,
        y: _constants.timeLineHeight - 10,
        w: _constants.channelProgramsWidth,
        h: h => h - _constants.timeLineHeight + 10,
        TimeStrip: {
          x: 0,
          w: 4,
          h: h => h,
          rect: true,
          color: Theme.Colors.Guide.currentTime,
          visible: false,
        },
      },
    };
  }

  _init() {
    this.tag('Timeline').backend = this.backend; // give Timeline a backend
    this.focusIdx = 0;

    const now = new Date();
    this.startTime = new Date(now.setMinutes(0, 0, 0));
    this.endTime = new Date(now.setHours(now.getHours() + 2, 0, 0, 0));
    Log.info('[EpgGuide] _init', this.startTime, this.endTime);
    this.tag('Timeline').updateTime(new Date(this.startTime), new Date(this.endTime));
    this.timeStripInterval = undefined;

    // make datasource that contains all the channels that need to be displayed
    this.datasource = new EpgGuideDataSource(this.backend);
    this._dummyItems = [];

    this.addItems();
  }

  _active() {
    this._positionTimeStrip();
  }

  _loadNext() {
    Registry.setTimeout(this.addItems.bind(this));
  }

  addItems() {
    // delete dummies
    this._dummyItems.forEach(el => {
      this.tag('Container').childList.remove(el);
    });
    this._dummyItems = [];
    // add items met ds.loadNext
    this.datasource.loadNext().then(channelDatasources => {
      channelDatasources.forEach(ds => {
        const yPos = this.calculateYPos(this.tag('Container').childList.length);
        this.tag('Container').childList.a({
          y: yPos,
          type: GuideChannel,
          backend: this.backend,
          datasource: ds,
          fromUntil: { start: this.startTime, end: this.endTime },
          initialCategories: this._categories,
        });
      });
      // add dummy if hasnext
      if (this.datasource.hasNext()) {
        const yPos = this.calculateYPos(this.tag('Container').childList.length);
        this._dummyItems.push(
          this.tag('Container').childList.a({
            y: yPos,
            type: GuideChannelDummy,
            backend: this.backend,
            signals: {
              loadNext: '_loadNext',
            },
          })
        );
      }
      this.tag('Container').h =
        this.tag('Container').childList.length * (_constants.channelHeight + _constants.channelSpacing);
    });
  }

  _positionTimeStrip() {
    if (this.timeStripInterval) {
      Registry.clearInterval(this.timeStripInterval);
      this.timeStripInterval = undefined;
    }
    const now = new Date();
    if (this.startTime <= now && now <= this.endTime) {
      this.tag('TimeStrip').visible = true;
      const pos =
        ((now.getTime() - this.startTime.getTime()) / (this.endTime.getTime() - this.startTime.getTime())) *
        _constants.channelProgramsWidth;
      this.tag('TimeStrip').patch({ x: pos });
      this.timeStripInterval = Registry.setInterval(() => {
        if (this.startTime <= now && now <= this.endTime) {
          const now = new Date();
          const pos = Math.round(
            ((now.getTime() - this.startTime.getTime()) / (this.endTime.getTime() - this.startTime.getTime())) *
              _constants.channelProgramsWidth
          );
          this.tag('TimeStrip').patch({ x: pos });
          // this.tag('TimeStrip').setSmooth('x', pos, { duration: 1 });
        } else {
          Registry.clearInterval(this.timeStripInterval);
          this.timeStripInterval = undefined;
        }
      }, 30000);
    } else {
      this.tag('TimeStrip').visible = false;
    }
  }

  _onDetach() {
    super._onDetach();
    if (this.timeStripInterval) {
      Registry.clearInterval(this.timeStripInterval);
      this.timeStripInterval = undefined;
    }
  }

  $moveRight() {
    // update current time to 2 hours ahead
    this.startTime.setHours(this.startTime.getHours() + 2);
    this.endTime.setHours(this.endTime.getHours() + 2);
    this._positionTimeStrip();

    this.tag('Container').children.forEach(channel => {
      channel.moveRight();
    });
    this.tag('Timeline').moveRight(this.startTime);
  }

  $moveLeft() {
    // update current time to 2 hours back
    this.startTime.setHours(this.startTime.getHours() - 2);
    this.endTime.setHours(this.endTime.getHours() - 2);
    this._positionTimeStrip();

    this.tag('Container').children.forEach(channel => {
      channel.moveLeft();
    });
    this.tag('Timeline').moveLeft(this.startTime);
  }

  updateTime(start, end) {
    this.startTime = new Date(start);
    this.endTime = new Date(end);
    // give everything updated start and end times and force reload
    this.tag('Timeline').updateTime(new Date(start));
    this.tag('Container').children.forEach(channel => {
      channel.setTime(start, end);
    });
    this._positionTimeStrip();
  }

  updateCategories(categorieArr) {
    this._categories = categorieArr;
    this.tag('Container').children.forEach(channel => {
      channel.setCategories(categorieArr);
    });
  }

  _handleDown() {
    let prevPos = this._getFocused().getCurrentPosition();

    let prevValue = this.focusIdx;
    this.focusIdx = Math.min(this.focusIdx + 1, this._getFocusable().length - 1);

    if (prevValue !== this.focusIdx) {
      this.centerOnIndex(this.focusIdx);
      this._getFocused().setPreferedFocus(prevPos);
      return true;
    }
    return false;
  }

  _handleUp() {
    let prevPos = this._getFocused().getCurrentPosition();

    let prevValue = this.focusIdx;
    this.focusIdx = Math.max(this.focusIdx - 1, 0);

    if (prevValue !== this.focusIdx) {
      this.centerOnIndex(this.focusIdx);
      this._getFocused().setPreferedFocus(prevPos);
      return true;
    }
    return false;
  }

  _getFocused() {
    let container = this.tag('Container');
    return container.children[this.focusIdx];
  }

  _getFocusable() {
    return this.tag('Container').children;
  }

  calculateYPos(index) {
    return index * (_constants.channelHeight + _constants.channelSpacing);
  }

  centerOnIndex(index) {
    const target = this.finalH / 2 - 150;
    const channelCenter = this.calculateYPos(index) + _constants.channelHeight / 2;
    const result = Math.min(0, target - channelCenter);
    this.tag('Container').setSmooth('y', result);
  }
}
